import ArrowDownIcon from '@/components/icons/ArrowDownIcon';
import { cn } from '@/lib/utils';
import { useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

interface Props {
    to: string;
    name: string;
    Icon: any;
    activeTab: string;
    setActiveTab: (value: string) => void;
    isSidebarOpen: boolean;
    secondaryActiveTab: string;
    setSecondaryActiveTab: (value: string) => void;
    navigation?: {
        name: string;
        to: string;
    }[];
    dropdownToggleOnly?: boolean;
    onLinkClick?: () => void;
}

const NavigationDropdown = ({
    to,
    name,
    Icon,
    activeTab,
    setActiveTab,
    isSidebarOpen,
    secondaryActiveTab,
    setSecondaryActiveTab,
    navigation = [],
    dropdownToggleOnly = false,
    onLinkClick,
}: Props) => {
    const { t } = useTranslation();
    const location = useLocation();
    const hasNavigation = navigation.length > 0;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isChildRouteActive = (itemTo: string) => {
        const [path, search] = itemTo.split('?');
        if (search) {
            return location.pathname === path && `?${search}` === location.search;
        }
        return location.pathname === itemTo;
    };

    useEffect(() => {
        if (hasNavigation && navigation.some((item) => isChildRouteActive(item.to))) {
            setIsDropdownOpen(true);
        }
    }, [location.pathname, location.search, hasNavigation, navigation]);

    const handleNavClick = () => {
        if (hasNavigation && !dropdownToggleOnly) {
            return;
        }
        setActiveTab(t(name));
        setSecondaryActiveTab('');
        onLinkClick?.();
    };

    const handleArrowClick = (event?: MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();
        setIsDropdownOpen((prev) => !prev);
        setActiveTab(t(name));
    };

    const parentClassName = cn(
        'w-full mb-1 flex items-center justify-between hover:text-primery-blue-700 rounded-s-lg py-4 ps-2 pe-4',
        {
            'bg-white-blue-50 text-primery-blue-500': activeTab === t(name),
            'hover:bg-white-blue-50': activeTab !== t(name),
        },
    );

    const arrowButton = isSidebarOpen ? (
        <button
            type="button"
            onClick={handleArrowClick}
            className="flex items-center justify-center rounded p-1 hover:bg-white-blue-100"
            aria-expanded={isDropdownOpen}
            aria-controls={`dropdown-${name}`}
            aria-label={t(name)}
        >
            <ArrowDownIcon
                className={cn('w-4 h-4 transition-transform', {
                    'rotate-180': isDropdownOpen,
                })}
            />
        </button>
    ) : null;

    const parentContent = (
        <>
            <div className="flex flex-row items-center">
                <Icon className={cn('w-6 h-6', { 'me-2': isSidebarOpen })} />
                {isSidebarOpen && <span className="text-start text-nowrap text-[14px]">{t(name)}</span>}
            </div>
            {hasNavigation ? arrowButton : null}
        </>
    );

    return (
        <>
            {hasNavigation && dropdownToggleOnly ? (
                <div className={parentClassName}>{parentContent}</div>
            ) : (
                <NavLink to={to} onClick={handleNavClick} className={parentClassName}>
                    {parentContent}
                </NavLink>
            )}
            {isDropdownOpen && isSidebarOpen && hasNavigation && (
                <div id={`dropdown-${name}`} className="flex flex-col">
                    {navigation.map((item) => (
                        <NavLink
                            to={item.to}
                            key={t(item.name)}
                            onClick={() => {
                                setSecondaryActiveTab(t(item.name));
                                setActiveTab(t(name));
                                onLinkClick?.();
                            }}
                            className={cn(
                                'w-full mb-1 flex items-center justify-between hover:text-primery-blue-700 rounded-s-lg py-4 ps-2 pe-4',
                                {
                                    'bg-primery-blue-900 text-white-blue-50 hover:bg-white-blue-50':
                                        secondaryActiveTab === t(item.name) && activeTab === t(name),
                                    'hover:bg-white-blue-50': secondaryActiveTab !== t(item.name),
                                },
                            )}
                        >
                            <span className={cn('ms-6 text-start', { hidden: !isSidebarOpen })}>{t(item.name)}</span>
                        </NavLink>
                    ))}
                </div>
            )}
        </>
    );
};

export default NavigationDropdown;
